import { computed, ref, toValue, type MaybeRefOrGetter } from 'vue'

import type { ValidationRole } from '@/types/approval-flow.types'

/**
 * Seleção do papel com que o usuário vota num fluxo de validação (aprovação ou
 * reversão de excepcionalidade).
 *
 * - um papel só: vota com ele, sem escolha;
 * - dois ou mais: o usuário precisa escolher (chips do `ApprovalRoleSelector`);
 * - nenhum: não há como votar.
 */
export function useApprovalRole(roles: MaybeRefOrGetter<ValidationRole[] | null | undefined>) {
  const availableRoles = computed<ValidationRole[]>(() => toValue(roles) ?? [])
  const selectedRole = ref<ValidationRole | null>(null)

  const needsRoleSelection = computed(() => availableRoles.value.length >= 2)

  const effectiveRole = computed<ValidationRole | undefined>(() => {
    if (availableRoles.value.length === 1) return availableRoles.value[0]
    return selectedRole.value ?? undefined
  })

  /** Há papéis para escolher, mas nenhum foi escolhido: o voto ainda não pode sair. */
  const isRoleMissing = computed(() => needsRoleSelection.value && !effectiveRole.value)

  function resetRole() {
    selectedRole.value = null
  }

  return {
    availableRoles,
    selectedRole,
    needsRoleSelection,
    effectiveRole,
    isRoleMissing,
    resetRole
  }
}
